import { useI18N } from '@yandex-int/i18n/lib/react';
import { useLocation } from 'react-router-dom';

import { getCanViewAsAdmin, getIsYangoRequest } from 'client/utils';
import * as keyset from 'i18n/Settings';

import { routes } from '../lib/routes';
import { MarkedMenuItem } from '../model/menu-item';
import { SkillAccess } from '../model/settings';
import { AliceApp, Channel } from '../model/skill';

import { useMenuItems } from './useMenuItems';

type SkillTab =
    | 'general'
    | 'settings'
    | 'test'
    | 'promotion'
    | 'resources'
    // | 'notifications'
    | 'billing'
    | 'smartHomeTest'
    | 'rating'
    | 'monitoring'
    | 'operations'
    | 'donation'
    | 'sharing'
    | 'editorFields'
    | 'authorization'
    | 'featureFlags'
    | 'adminSettings';

const isSharingPageEnabled = (aliceApp: AliceApp) =>
    [Channel.AliceSkill, Channel.SmartHome].includes(aliceApp.channel) &&
    (aliceApp.skillAccess === SkillAccess.Private || aliceApp.draft.skillAccess === SkillAccess.Private) &&
    !(getIsYangoRequest() && !__appConfig.user.isAdmin);

export const useSkillPageMenuItems = (aliceApp: AliceApp) => {
    const location = useLocation();
    const t = useI18N(keyset);

    const canViewAsAdmin = getCanViewAsAdmin();

    // ad-hoc: refactor later (get rid of a large 'if' statement)
    const display: Record<SkillTab, boolean> = {
        general: false,
        settings: false,
        test: aliceApp.channel === Channel.AliceSkill,
        promotion: [Channel.AliceSkill].includes(aliceApp.channel),
        resources: aliceApp.channel === Channel.AliceSkill,
        billing: aliceApp.channel === Channel.AliceSkill && Boolean(__appConfig.user.featureFlags.allowUseBillingPage),
        smartHomeTest: aliceApp.channel === Channel.SmartHome,
        rating: true,
        monitoring: true,
        donation: true,
        sharing: isSharingPageEnabled(aliceApp),
        editorFields: [Channel.AliceSkill, Channel.SmartHome].includes(aliceApp.channel) && canViewAsAdmin,
        featureFlags:
            [Channel.AliceSkill, Channel.SmartHome, Channel.TvApp, Channel.SmartScreenApp].includes(aliceApp.channel) &&
            canViewAsAdmin,
        authorization: [Channel.AliceSkill, Channel.SmartHome].includes(aliceApp.channel),
        adminSettings: canViewAsAdmin && [Channel.AliceSkill].includes(aliceApp.channel),
        operations: true,
    };
    const routesWithPublishedState = [
        routes.app.skillPage(false, 'settings', aliceApp.id),
        routes.app.skillPage(false, 'test', aliceApp.id),
    ];

    const isDraft = !routesWithPublishedState.some((path) => location.pathname.startsWith(path));

    const items: Array<MarkedMenuItem<SkillTab>> = [
        {
            tab: 'general',
            path: routes.app.skill(aliceApp.id),
            exact: true,
            title: t('menu-general'),
        },
        {
            tab: 'settings',
            path: routes.app.skillPage(isDraft, 'settings', aliceApp.id),
            title: t('menu-settings'),
        },
        {
            tab: 'authorization',
            path: routes.app.skillPage(false, 'authorization', aliceApp.id),
            title: t('menu-account-link'),
        },
        {
            tab: 'resources',
            path: routes.app.skillResources('', aliceApp.id),
            title: t('menu-resources'),
        },
        {
            tab: 'test',
            path: routes.app.skillPage(isDraft, 'test', aliceApp.id),
            title: t('menu-test'),
        },
        {
            tab: 'smartHomeTest',
            path: routes.app.skillPage(isDraft, 'test', aliceApp.id),
            title: t('menu-test'),
        },
        {
            tab: 'rating',
            path: routes.app.skillRating(aliceApp.id),
            title: t('menu-rating'),
        },
        {
            tab: 'promotion',
            path: routes.app.skillPage(false, 'promotion', aliceApp.id),
            title: t('menu-promo'),
        },
        {
            tab: 'billing',
            path: routes.app.skillPage(false, 'billing', aliceApp.id),
            title: t('menu-pay'),
        },
        {
            tab: 'monitoring',
            path: routes.app.skillPage(false, 'monitoring', aliceApp.id),
            title: t('menu-monitoring'),
        },
        {
            tab: 'donation',
            path: routes.app.skillPage(false, 'donation', aliceApp.id),
            title: t('menu-donation'),
        },
        {
            tab: 'sharing',
            path: routes.app.skillPage(false, 'sharing', aliceApp.id),
            title: t('menu-access'),
        },
        {
            tab: 'editorFields',
            path: routes.app.skillPage(false, 'editing', aliceApp.id),
            title: t('menu-editors'),
        },
        {
            tab: 'featureFlags',
            path: routes.app.skillPage(false, 'feature-flags', aliceApp.id),
            title: t('feature-flags'),
        },
        {
            tab: 'adminSettings',
            path: routes.app.skillPage(false, 'admin-settings', aliceApp.id),
            title: t('admin-settings'),
        },
        {
            tab: 'operations',
            path: routes.app.skillPage(false, 'operations', aliceApp.id),
            title: t('events'),
        },
    ];

    return useMenuItems(display, items);
};
